import React, { useEffect, useState, useCallback } from 'react';
import { Leaf, Megaphone, Eye, Heart, FileText, MessageSquare, DollarSign, MousePointerClick, Percent } from 'lucide-react';
import { fmtNum, fmtSpend, fmtPct } from '../lib/metaStatsFormat';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function MetricRow({ icon: Icon, label, value, detail }) {
  return (
    <div className="mstat-cmp-metric">
      <div className="mstat-cmp-metric-icon"><Icon size={14} /></div>
      <div>
        <p className="mstat-cmp-metric-value">{value}</p>
        <p className="mstat-cmp-metric-label">{label}</p>
        {detail && <p className="mstat-cmp-metric-detail">{detail}</p>}
      </div>
    </div>
  );
}

export default function MetaStatsComparisonSummary({ accessToken, marketsParam }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(`${API_URL}/api/meta-stats/comparison?markets=${marketsParam}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setData(await r.json());
    } catch (e) {
      setError('Kunde inte hämta jämförelsedata.');
    } finally {
      setLoading(false);
    }
  }, [accessToken, marketsParam]);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) return <div className="mstat-cmp-card mstat-cmp-card--loading" data-testid="metastats-comparison-loading">Laddar organisk/betald-jämförelse...</div>;
  if (error && !data) return <div className="admin-notice" data-testid="metastats-comparison-error">{error}</div>;
  if (!data) return null;

  const { organic, paid } = data;
  const orgReach = organic.reach_instagram_only || 0;
  const paidReach = paid.reach || 0;
  const totalReach = orgReach + paidReach;
  const orgPctRaw = totalReach > 0 ? (orgReach / totalReach) * 100 : 0;
  const paidPctRaw = 100 - orgPctRaw;
  const orgPct = Math.round(orgPctRaw * 10) / 10;
  const paidPct = Math.round(paidPctRaw * 10) / 10;
  // Minsta synliga bredd på staplen så en liten men verklig andel inte ser ut som "0" i UI:t
  const orgBarWidth = totalReach > 0 ? Math.max(orgPctRaw, orgPctRaw > 0 ? 1.5 : 0) : 0;
  const paidBarWidth = 100 - orgBarWidth;

  return (
    <div className="mstat-cmp-card" data-testid="metastats-comparison-summary" style={{ opacity: loading ? 0.6 : 1, transition: 'opacity 0.2s ease' }}>
      <div className="mstat-cmp-grid">
        <div className="mstat-cmp-col mstat-cmp-col--organic">
          <div className="mstat-cmp-col-header">
            <span className="mstat-badge mstat-badge--organic"><Leaf size={11} /> Organiskt innehåll (FB &amp; IG)</span>
          </div>
          <h3 className="mstat-cmp-title">Organisk prestanda (30 dagar)</h3>
          <MetricRow icon={Heart} label="Totalt engagemang" value={fmtNum(organic.engagement_total)} detail="Reaktioner + kommentarer + delningar" />
          <div className="mstat-cmp-metric-subgrid">
            <MetricRow icon={FileText} label="Publicerade inlägg" value={fmtNum(organic.post_count)} />
            <MetricRow icon={MessageSquare} label="Kommentarer/inlägg" value={organic.comments_per_post ?? '–'} />
          </div>
          <MetricRow icon={Eye} label="Räckvidd (endast Instagram)" value={fmtNum(organic.reach_instagram_only)} detail="Facebook saknar räckviddsdata i vår datakälla" />
        </div>

        <div className="mstat-cmp-divider" aria-hidden="true" />

        <div className="mstat-cmp-col mstat-cmp-col--paid">
          <div className="mstat-cmp-col-header">
            <span className="mstat-badge mstat-badge--paid"><Megaphone size={11} /> Meta Ads (FB &amp; IG)</span>
          </div>
          <h3 className="mstat-cmp-title">Betald annonsering (senaste ögonblicksbild)</h3>
          <MetricRow icon={Eye} label="Total annonsräckvidd" value={fmtNum(paid.reach)} detail="Unika personer nådda via betalda kampanjer" />
          <div className="mstat-cmp-metric-subgrid">
            <MetricRow icon={DollarSign} label="Total spend" value={`${fmtSpend(paid.spend)} kr`} />
            <MetricRow icon={MousePointerClick} label="Klick / CTR" value={`${fmtNum(paid.clicks)} / ${fmtPct(paid.ctr)}`} />
          </div>
          <MetricRow icon={Percent} label="Köp &amp; kostnad/köp" value={`${fmtNum(paid.kop)} st · ${paid.kostnad_per_kop != null ? `${fmtSpend(paid.kostnad_per_kop)} kr` : '–'}`} detail={paid.note} />
        </div>
      </div>

      <div className="mstat-cmp-ratio" data-testid="metastats-comparison-ratio">
        <span className="mstat-cmp-ratio-label">Räckviddsmix: Organisk (30 dagar) vs Betald (senaste ögonblicksbild)</span>
        <div className="mstat-cmp-ratio-bar">
          <div className="mstat-cmp-ratio-fill mstat-cmp-ratio-fill--organic" style={{ width: `${orgBarWidth}%` }} title={`Organisk: ${orgPct}%`} />
          <div className="mstat-cmp-ratio-fill mstat-cmp-ratio-fill--paid" style={{ width: `${paidBarWidth}%` }} title={`Betald: ${paidPct}%`} />
        </div>
        <div className="mstat-cmp-ratio-legend">
          <span><i className="mstat-cmp-dot mstat-cmp-dot--organic" /> Organisk {orgPct}%</span>
          <span><i className="mstat-cmp-dot mstat-cmp-dot--paid" /> Betald {paidPct}%</span>
        </div>
      </div>
    </div>
  );
}
